/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import axios, { CancelTokenSource } from 'axios';
import cx from 'classnames';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';

import { ClusterStates, Endpoints, Infer } from 'shared';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { timeUnitMultipliers } from '../../../../constants/timeUnitMultipliers';
import { Namespaces } from '../../../../i18n';
import { TimeUnits } from '../../../../miscellaneous/timeUnits';
import { ClusterApiService } from '../../../../services/ClusterApiService';
import { enqueueHUSnackbar } from '../../../../store/notifier/actions';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { ClusterNodeListResponsePayload } from '../../../../types/cluster/ClusterNodeListResponsePayload';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { reactToRestartClusterNode } from '../../../../utils/reactions';
import { saveFile } from '../../../../utils/saveFile';
import { jwtToken } from '../../../../utils/storageHelpers';
import { useProjectPermissions } from '../../../../utils/useProjectPermissions';
import { NodeExpansionPanel } from '../NodeExpansionPanel';
import { DownloadNodeLogsForm } from './components/DownloadNodeLogsForm/DownloadNodeLogsForm';
import { FormValues } from './components/DownloadNodeLogsForm/DownloadNodeLogsForm.types';
import { createStatusTabStyles } from './ReplicasTab.styles';
import {
  DownloadableNodeType,
  HandlePopoverToggleArgs,
  HandleRestartModalArgs,
  RestartableNodeType,
  RestartNodeErrorModalType,
} from './ReplicasTab.types';

const getUnitSeconds = (time: number, unit: TimeUnits): number => {
  const multiplier = timeUnitMultipliers[unit] || timeUnitMultipliers.__fallback;
  return time * multiplier;
};

type ReplicasTabProps = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  cluster: Infer<typeof Endpoints.GetCluster>['Response'][200];
  status: ClusterStates | undefined;
  nodes: ClusterNodeListResponsePayload[];
  isLoadingNodes: boolean;
};

export function ReplicasTab(props: ReplicasTabProps) {
  const { identificationData, cluster, status, nodes, isLoadingNodes } = props;
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = createStatusTabStyles();
  const dispatch = useDispatch();

  const permissions = useProjectPermissions(identificationData.organizationId, identificationData.projectId);

  const restartCancelToken = useRef<null | CancelTokenSource>(null);

  const wantToDownloadInit: DownloadableNodeType = {
    id: null,
    anchor: null,
    isOpen: false,
  };
  const wantToRestartInit: RestartableNodeType = {
    id: null,
    role: null,
    isOpen: false,
  };

  const errorModalInit: RestartNodeErrorModalType = {
    isOpen: false,
    message: '',
    id: null,
    role: null,
  };

  const [isDownloading, setIsDownloading] = useState(false);
  const [wantToDownload, setWantToDownload] = useState<DownloadableNodeType>(wantToDownloadInit);
  const [wantToRestart, setWantToRestart] = useState<RestartableNodeType>(wantToRestartInit);
  const [processingRestart, setProcessingRestart] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<RestartNodeErrorModalType>(errorModalInit);

  const handlePopoverToggle = (args: HandlePopoverToggleArgs) => {
    const { event, id } = args;
    const isPopoverOpen = wantToDownload.isOpen;
    setWantToDownload({
      id: !isPopoverOpen ? id : null,
      anchor: !isPopoverOpen ? event.currentTarget : null,
      isOpen: !isPopoverOpen,
    });
  };

  const handlePopoverCancel = () => {
    setWantToDownload(wantToDownloadInit);
  };

  const handlePopoverDownload = async (values: FormValues) => {
    const { time, unit } = values;
    if (wantToDownload.id && time && unit) {
      const seconds = getUnitSeconds(time, unit);
      handlePopoverCancel();
      setIsDownloading(true);
      try {
        const headers = new Headers({ authorization: `Bearer ${jwtToken.get()}` });
        const response = await window.fetch(
          ClusterApiService.getDownloadClusterLogsNodeUrl({
            ...identificationData,
            nodeId: wantToDownload.id,
            sinceMinutes: seconds,
          }),
          { headers },
        );
        await saveFile(await response.blob(), `${cluster.name}.txt`);
      } catch (e) {
        const error = e as ApiClientError;
        console.error('failed to download node log', error);
        dispatch(enqueueHUSnackbar({ primaryText: t(`common:errors.unexpected_error`) }, { variant: 'error' }));
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const handleRestartModal = (args: HandleRestartModalArgs) => {
    setWantToRestart({ id: args.id, role: args.role, isOpen: true });
  };

  const handleSubmitRestart = async () => {
    const { id } = wantToRestart;
    try {
      setProcessingRestart(true);
      if (id) {
        restartCancelToken.current = axios.CancelToken.source();
        await ClusterApiService.restartClusterNode(
          {
            ...identificationData,
            nodeId: id,
          },
          { cancelToken: restartCancelToken.current.token },
        );
      }
      setProcessingRestart(false);
      setWantToRestart(wantToRestartInit);
      await reactToRestartClusterNode(identificationData);
    } catch (e) {
      const error = e as ApiClientError;
      const errorMessage = error.response?.data.message;
      setProcessingRestart(false);
      setWantToRestart(wantToRestartInit);
      if (!axios.isCancel(e)) {
        setErrorModal({
          isOpen: true,
          message: errorMessage ?? '',
          id: wantToRestart.id,
          role: wantToRestart.role,
        });
      }
    } finally {
      restartCancelToken.current = null;
    }
  };

  const handleCancelRestart = async () => {
    if (restartCancelToken.current) {
      restartCancelToken.current.cancel();
    }
    restartCancelToken.current = null;
    setProcessingRestart(false);
    setWantToRestart(wantToRestartInit);
    await reactToRestartClusterNode(identificationData);
  };

  return (
    <>
      {isLoadingNodes ? (
        <AlignCenter>
          <CircularProgress color="primary" size={40} />
        </AlignCenter>
      ) : (
        <Grid container classes={{ root: classes.root }}>
          <Grid container classes={{ root: classes.header }}>
            <Grid item xs={2}>
              <Typography className={classes.headerItem}>
                {t(`${Namespaces.Cluster}:details.replicas.instance`)}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.headerItem}>{t(`${Namespaces.Cluster}:details.replicas.role`)}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.headerItem}>{t(`${Namespaces.Cluster}:details.replicas.lag`)}</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.headerItem}>
                {t(`${Namespaces.Cluster}:details.replicas.status`)}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.headerItem}>{t(`${Namespaces.Cluster}:details.storage.title`)}</Typography>
            </Grid>
            <Grid item xs={2}>
              <div className={cx(classes.headerItem, classes.lastHeaderItems)}>
                {permissions.edit_cluster ? (
                  <Typography className={classes.headerItem}>
                    {t(`${Namespaces.Cluster}:details.replicas.actions`)}
                  </Typography>
                ) : null}

                <Typography className={classes.headerItem}>
                  {t(`${Namespaces.Cluster}:details.replicas.logs`)}
                </Typography>
              </div>
            </Grid>
          </Grid>
          <Box className={classes.section}>
            <Grid container spacing={0}>
              {nodes.map(node => {
                return (
                  <NodeExpansionPanel
                    key={node.id}
                    node={node}
                    identificationData={identificationData}
                    handlePopoverToggle={handlePopoverToggle}
                    handleRestartModal={handleRestartModal}
                    clusterStatus={status}
                    isDownloadingLogs={isDownloading}
                  />
                );
              })}
            </Grid>
          </Box>
        </Grid>
      )}
      <Popover onClose={handlePopoverCancel} open={wantToDownload.isOpen} anchorEl={wantToDownload.anchor}>
        <Paper classes={{ root: classes.popover }}>
          <DownloadNodeLogsForm onSubmit={handlePopoverDownload} onCancel={handlePopoverCancel} />
        </Paper>
      </Popover>
      <SimpleConfirmModal
        message={t('details.replicas.restart.title', {
          instanceId: wantToRestart.id ?? 0,
          instanceRole: t([`details.replicas.roles.${wantToRestart.role}`, 'details.replicas.roles.master']),
        })}
        open={wantToRestart.isOpen}
        {...(processingRestart
          ? {
              onConfirm: () => {},
              confirmButtonText: (
                <>
                  &nbsp; <CircularProgress color="primary" size={24} />
                </>
              ),
              confirmButtonProps: { disabled: true },
              onCancel: handleCancelRestart,
              cancelButtonText: t('details.replicas.restart.cancel_button_label'),
            }
          : {
              onConfirm: handleSubmitRestart,
              onCancel: () => setWantToRestart(wantToRestartInit),
            })}
      />
      <SimpleNotifyModal
        open={errorModal.isOpen}
        message={t(
          [
            `${Namespaces.Common}:node_restart_errors.${errorModal.message}`,
            `${Namespaces.Common}:errors.unexpected_error`,
          ],
          {
            instanceId: errorModal.id,
            instanceRole: t(`details.replicas.roles.${errorModal.role}`),
          },
        )}
        onOk={() => setErrorModal(errorModalInit)}
      />
    </>
  );
}
